import INVENTORY_API_BASE_URL from "../../config/api";
import { apiFetch } from "../../config/apiClient";
import React, { useState } from "react";
import "./BooklifeCycle.css";

function BooklifeCycle() {
  const [searchValue, setSearchValue] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formatDate = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "-";

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    if (!searchValue.trim()) {
      setError("Enter a barcode or value to search.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const response = await apiFetch(
        `${INVENTORY_API_BASE_URL}/searchCycle?value=${encodeURIComponent(searchValue.trim())}`,
      );
      const data = await response.json();
      const sortedResult = { ...data };

      const unifiedTimeline = [];

      const knownDateKeys = [
        "date",
        "Dated",
        "updatedAt",
        "createdAt",
        "orderdate",
      ];

      Object.entries(sortedResult).forEach(([key, value]) => {
        if (typeof value !== "object" || value === null) return;
        if (
          ["barcodeHistory", "outboundInfo", "returnInboundInfo"].includes(key)
        )
          return;

        const labelMap = {
          POInfo: "Shivalik PO Info",
          studentRegisterResult: "Student-Register Info",
          shelfInfo: "Shelf Status",
        };

        if (Array.isArray(value)) {
          value.forEach((item) => {
            for (let dateKey of knownDateKeys) {
              if (typeof item[dateKey] === "string") {
                const parsedDate = new Date(item[dateKey]);
                if (!isNaN(parsedDate.getTime())) {
                  unifiedTimeline.push({
                    label: labelMap[key] || key,
                    data: item,
                    date: parsedDate,
                    display: formatDate(item[dateKey]),
                  });
                  break;
                }
              }
            }
          });
        } else {
          for (let dateKey of knownDateKeys) {
            if (typeof value[dateKey] === "string") {
              const parsedDate = new Date(value[dateKey]);
              if (!isNaN(parsedDate.getTime())) {
                unifiedTimeline.push({
                  label: labelMap[key] || key,
                  data: value,
                  date: parsedDate,
                  display: formatDate(value[dateKey]),
                });
                break;
              }
            }
          }
        }
      });
      if (Array.isArray(sortedResult.barcodeHistory)) {
        sortedResult.barcodeHistory.forEach((entry, index) => {
          const parsedDate = new Date(entry.date);
          if (!isNaN(parsedDate.getTime())) {
            unifiedTimeline.push({
              label:
                entry.collection === "ScanInbound" ? "Scan-Inbound" : "B2C",
              data: entry,
              date: parsedDate,
              display: formatDate(entry.date),
            });
          }
        });
      }

      if (Array.isArray(sortedResult.outboundInfo)) {
        sortedResult.outboundInfo.forEach((entry, index) => {
          const dateField = entry.Dated || entry.orderdate;
          const parsedDate = new Date(dateField);
          if (!isNaN(parsedDate.getTime())) {
            unifiedTimeline.push({
              label: "Distributor-Outbound Info",
              data: entry,
              date: parsedDate,
              display: formatDate(dateField),
            });
          }
        });
      }

      if (Array.isArray(sortedResult._unifiedTimeline)) {
        sortedResult._unifiedTimeline = sortedResult._unifiedTimeline.map(
          (entry) => {
            if (
              entry.label === "Student-Register Info" &&
              result?.studentRegisterResult
            ) {
              const student = result.studentRegisterResult;
              const book = student.Books_Purchased?.find(
                (b) => b?.searchBarcode === searchValue,
              );

              if (book) {
                const keysToInclude = [
                  "pincode",
                  "city",
                  "OnlineMode",
                  "OrderNum",
                  "OnlineSeller",
                  "BookShop",
                  "BookshopAddress",
                ];

                keysToInclude.forEach((key) => {
                  if (
                    book[key] !== "" &&
                    book[key] !== null &&
                    book[key] !== undefined
                  ) {
                    entry.data[key] = book[key];
                  }
                });
              }
            }
            return entry;
          },
        );
      }

      unifiedTimeline.sort((a, b) => a.date.getTime() - b.date.getTime());

      sortedResult._unifiedTimeline = unifiedTimeline;
      setResult(sortedResult);
    } catch (err) {
      setError(err.message || "Unable to load this book history.");
      console.error("Search failed", err);
    } finally {
      setLoading(false);
    }
  };

  const renderDetails = (data, excludeDates = false) => {
    const dateFields = ["date", "Dated", "updatedAt", "createdAt", "orderdate"];
    const details = Object.entries(
      data && typeof data === "object" ? data : {},
    ).filter(([key]) => !excludeDates || !dateFields.includes(key));

    if (details.length === 0) {
      return <p className="bookLifecycle__noDetails">No additional details.</p>;
    }

    return (
      <dl className="bookLifecycle__details">
        {details.map(([key, value]) => (
          <div className="bookLifecycle__detail" key={key}>
            <dt>{key}</dt>
            <dd>
              {typeof value === "object"
                ? JSON.stringify(value, null, 2)
                : String(value)}
            </dd>
          </div>
        ))}
      </dl>
    );
  };

  const timeline = Array.isArray(result?._unifiedTimeline)
    ? result._unifiedTimeline
    : [];

  return (
    <main className="bookLifecycle">
      <div className="bookLifecycle__page">
        <header className="bookLifecycle__header">
          <div>
            <span className="bookLifecycle__eyebrow">INVENTORY INTELLIGENCE</span>
            <h1>Book life cycle</h1>
            <p>
              Follow a book’s movement from sticker creation through receiving,
              shelf storage, and outbound activity.
            </p>
          </div>
          <span className="bookLifecycle__headerBadge">
            <span aria-hidden="true" />
            Lifecycle lookup
          </span>
        </header>

        <form
          className="bookLifecycle__search"
          onSubmit={handleSearch}
          aria-busy={loading}
        >
          <div className="bookLifecycle__searchCopy">
            <span className="bookLifecycle__sectionEyebrow">RECORD LOOKUP</span>
            <h2>Find a book record</h2>
            <p>Search with a barcode or another value linked to the book.</p>
          </div>
          <div className="bookLifecycle__searchControls">
            <label htmlFor="book-lifecycle-search">Barcode or search value</label>
            <div className="bookLifecycle__inputRow">
              <div className="bookLifecycle__inputWrap">
                <span aria-hidden="true">⌕</span>
                <input
                  id="book-lifecycle-search"
                  type="search"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  placeholder="Enter barcode or value"
                  autoComplete="off"
                />
              </div>
              <button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <span
                      className="bookLifecycle__spinner"
                      aria-hidden="true"
                    />
                    Searching…
                  </>
                ) : (
                  <>
                    Search lifecycle
                    <span aria-hidden="true">→</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {error && (
          <div className="bookLifecycle__error" role="alert">
            <span aria-hidden="true">!</span>
            <div>
              <strong>Search could not be completed</strong>
              <p>{error}</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="bookLifecycle__loading" role="status">
            Retrieving book history…
          </div>
        )}

        {result && (
          <div className="bookLifecycle__results">
            <section
              className="bookLifecycle__summary"
              aria-label="Search summary"
            >
              <article className="bookLifecycle__summaryCard bookLifecycle__summaryCard--search">
                <span className="bookLifecycle__summaryIcon" aria-hidden="true">
                  #
                </span>
                <div>
                  <span>SEARCH VALUE</span>
                  <strong title={searchValue}>{searchValue || "—"}</strong>
                </div>
              </article>
              <article className="bookLifecycle__summaryCard">
                <span className="bookLifecycle__summaryIcon bookLifecycle__summaryIcon--blue" aria-hidden="true">
                  ◷
                </span>
                <div>
                  <span>TIMELINE EVENTS</span>
                  <strong>{timeline.length.toLocaleString()}</strong>
                </div>
              </article>
              <article className="bookLifecycle__summaryCard">
                <span className="bookLifecycle__summaryIcon bookLifecycle__summaryIcon--green" aria-hidden="true">
                  ✓
                </span>
                <div>
                  <span>RECORD STATUS</span>
                  <strong>{timeline.length ? "Activity found" : "No dated events"}</strong>
                </div>
              </article>
            </section>

            {(result.randomCodeResult || result.shelfInfo) && (
              <div className="bookLifecycle__snapshotGrid">
                {result.randomCodeResult && (
                  <section
                    className="bookLifecycle__snapshot"
                    aria-label="Sticker information"
                  >
                    <header className="bookLifecycle__sectionHeader">
                      <div>
                        <span className="bookLifecycle__sectionEyebrow">
                          IDENTIFICATION
                        </span>
                        <h2>Sticker information</h2>
                      </div>
                      <span className="bookLifecycle__sectionIcon" aria-hidden="true">
                        #
                      </span>
                    </header>
                    {renderDetails(result.randomCodeResult)}
                  </section>
                )}

                {result.shelfInfo && (
                  <section
                    className="bookLifecycle__snapshot"
                    aria-label="Shelf information"
                  >
                    <header className="bookLifecycle__sectionHeader">
                      <div>
                        <span className="bookLifecycle__sectionEyebrow">
                          CURRENT LOCATION
                        </span>
                        <h2>Shelf information</h2>
                      </div>
                      <span className="bookLifecycle__sectionIcon bookLifecycle__sectionIcon--green" aria-hidden="true">
                        ▦
                      </span>
                    </header>
                    {renderDetails(result.shelfInfo)}
                  </section>
                )}
              </div>
            )}

            <section
              className="bookLifecycle__timelineSection"
              aria-label="Book lifecycle timeline"
            >
              <header className="bookLifecycle__sectionHeader">
                <div>
                  <span className="bookLifecycle__sectionEyebrow">
                    CHRONOLOGICAL HISTORY
                  </span>
                  <h2>Lifecycle timeline</h2>
                  <p>Events are shown from earliest to latest.</p>
                </div>
                <span className="bookLifecycle__eventCount">
                  {timeline.length.toLocaleString()}{" "}
                  {timeline.length === 1 ? "event" : "events"}
                </span>
              </header>

              {timeline.length > 0 ? (
                <ol className="bookLifecycle__timeline">
                  {timeline.map((event, index) => (
                    <li
                      className="bookLifecycle__timelineItem"
                      key={event.label + event.display + index}
                    >
                      <span
                        className="bookLifecycle__timelineRail"
                        aria-hidden="true"
                      >
                        <span />
                      </span>
                      <article className="bookLifecycle__eventCard">
                        <header className="bookLifecycle__eventHeader">
                          <span className="bookLifecycle__source">
                            {event.label}
                          </span>
                          <time className="bookLifecycle__eventDate">
                            {event.display}
                          </time>
                        </header>
                        {renderDetails(event.data, true)}
                      </article>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="bookLifecycle__empty">
                  <span aria-hidden="true">◷</span>
                  <div>
                    <strong>No dated events found</strong>
                    <p>
                      This record is available, but it has no timestamped
                      lifecycle activity to show.
                    </p>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </main>
  );
}

export default BooklifeCycle;
